import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, MiniMap, MarkerType } from "reactflow";
import "reactflow/dist/style.css";

/**
 * Project 12: Cloud Data Lakehouse on AWS (Medallion Architecture)
 * Bronze/Silver/Gold lakehouse with DMS CDC, Lambda API ingestion, SFTP,
 * PySpark cleansing, Glue star schema (SCD2), Athena serving, 7-check DQ layer.
 */

const nodeStyle = {
  section: {
    background: "#ffffff",
    border: "2px solid #cbd5e1",
    borderRadius: 12,
    padding: 6,
    fontWeight: 800,
    fontSize: 12,
    color: "#0f172a",
    width: 220,
    textAlign: "center",
  },
  compute: {
    background: "#e7f5ff",
    border: "2px solid #1c7ed6",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#0b2e59",
    width: 170,
    textAlign: "center",
  },
  data: {
    background: "#fff0f6",
    border: "2px solid #a61e4d",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#4a0b2a",
    width: 170,
    textAlign: "center",
  },
  ops: {
    background: "#f3fff6",
    border: "2px solid #2f9e44",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#0f3d1f",
    width: 190,
    textAlign: "center",
  },
  note: {
    background: "#fffbeb",
    border: "2px solid #f59e0b",
    borderRadius: 10,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#92400e",
    width: 200,
    textAlign: "center",
  },
  net: {
    background: "#e3fafc",
    border: "2px solid #1098ad",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#05333b",
    width: 170,
    textAlign: "center",
  },
};

function mkEdge(id, source, target, label, opts = {}) {
  return {
    id,
    source,
    target,
    label,
    type: "smoothstep",
    animated: opts?.animated ?? false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: opts?.style?.stroke ?? "#64748b",
    },
    labelStyle: { fontWeight: 700, fill: "#0f172a" },
    labelBgStyle: { fill: "#ffffff" },
    labelBgPadding: [6, 4],
    style: {
      strokeWidth: 4,
      stroke: "#64748b",
      ...(opts?.style ?? {}),
    },
    ...opts,
  };
}

export default function Project12FlowDiagram() {
  const nodes = useMemo(
    () => [
      // Background groups
      {
        id: "bg-sources",
        position: { x: 60, y: 40 },
        data: { label: "" },
        style: {
          width: 500,
          height: 640,
          background: "#eff6ff40",
          border: "3px solid #93c5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-bronze",
        position: { x: 620, y: 40 },
        data: { label: "" },
        style: {
          width: 460,
          height: 640,
          background: "#fff7ed40",
          border: "3px solid #fdba74",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-silver",
        position: { x: 1140, y: 40 },
        data: { label: "" },
        style: {
          width: 500,
          height: 640,
          background: "#f8fafc60",
          border: "3px solid #cbd5e1",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-gold",
        position: { x: 1700, y: 40 },
        data: { label: "" },
        style: {
          width: 520,
          height: 640,
          background: "#fefce840",
          border: "3px solid #fde047",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },

      // Section titles
      { id: "sec-sources", position: { x: 200, y: 50 }, data: { label: "Sources (3 ingestion paths)" }, style: nodeStyle.section },
      { id: "sec-bronze", position: { x: 740, y: 50 }, data: { label: "Bronze (raw)" }, style: nodeStyle.section },
      { id: "sec-silver", position: { x: 1280, y: 50 }, data: { label: "Silver (validated)" }, style: nodeStyle.section },
      { id: "sec-gold", position: { x: 1850, y: 50 }, data: { label: "Gold (serve + observe)" }, style: nodeStyle.section },

      // Sources
      { id: "s1", position: { x: 210, y: 140 }, data: { label: "PostgreSQL OLTP" }, style: nodeStyle.data },
      { id: "s2", position: { x: 210, y: 230 }, data: { label: "AWS DMS (CDC)" }, style: nodeStyle.net },
      { id: "s3", position: { x: 210, y: 330 }, data: { label: "Payments REST API" }, style: nodeStyle.compute },
      { id: "s4", position: { x: 210, y: 420 }, data: { label: "Lambda + EventBridge schedule" }, style: { ...nodeStyle.ops, width: 200 } },
      { id: "s5", position: { x: 210, y: 520 }, data: { label: "Partner files (SFTP)" }, style: nodeStyle.compute },
      { id: "s6", position: { x: 210, y: 610 }, data: { label: "AWS Transfer Family" }, style: nodeStyle.net },

      // Bronze
      { id: "b1", position: { x: 750, y: 160 }, data: { label: "S3 Bronze (raw, Parquet)" }, style: { ...nodeStyle.data, width: 200 } },
      { id: "b2", position: { x: 750, y: 280 }, data: { label: "Glue Data Catalog" }, style: nodeStyle.ops },
      { id: "b3", position: { x: 750, y: 380 }, data: { label: "Glue Schema Registry" }, style: nodeStyle.ops },
      { id: "b4", position: { x: 750, y: 480 }, data: { label: "Step Functions orchestration" }, style: { ...nodeStyle.net, width: 200 } },

      // Silver
      { id: "v1", position: { x: 1270, y: 140 }, data: { label: "PySpark cleansing (EC2)" }, style: { ...nodeStyle.compute, width: 200 } },
      { id: "v2", position: { x: 1270, y: 240 }, data: { label: "7-check data quality layer" }, style: { ...nodeStyle.note, width: 210 } },
      { id: "v3", position: { x: 1270, y: 350 }, data: { label: "Quarantine + reason code + run_id" }, style: { ...nodeStyle.note, width: 210 } },
      { id: "v4", position: { x: 1270, y: 460 }, data: { label: "S3 Silver (validated)" }, style: nodeStyle.data },
      { id: "v5", position: { x: 1270, y: 560 }, data: { label: "SHA-256 PII masking" }, style: nodeStyle.ops },

      // Gold + observability
      { id: "g1", position: { x: 1860, y: 140 }, data: { label: "Glue: star schema + SCD Type 2" }, style: { ...nodeStyle.compute, width: 210 } },
      { id: "g2", position: { x: 1860, y: 250 }, data: { label: "S3 Gold" }, style: nodeStyle.data },
      { id: "g3", position: { x: 1860, y: 340 }, data: { label: "Athena (1-2s queries)" }, style: nodeStyle.net },
      { id: "g4", position: { x: 1860, y: 430 }, data: { label: "KPIs: $85M revenue, 2.49% fraud" }, style: { ...nodeStyle.note, width: 210 } },
      { id: "g5", position: { x: 1860, y: 540 }, data: { label: "Splunk + CloudWatch + Firehose" }, style: { ...nodeStyle.ops, width: 210 } },
      { id: "g6", position: { x: 1860, y: 630 }, data: { label: "Cost cut 69% ($371 -> $116/mo)" }, style: { ...nodeStyle.note, width: 210 } },
    ],
    []
  );

  const edges = useMemo(() => {
    const blue = { stroke: "#2563eb" };
    const orange = { stroke: "#f59e0b" };
    const green = { stroke: "#16a34a" };
    const purple = { stroke: "#7c3aed" };

    return [
      // Sources internal
      mkEdge("se1", "s1", "s2", "CDC stream", { style: { ...blue } }),
      mkEdge("se2", "s3", "s4", "scheduled pull", { style: { ...blue } }),
      mkEdge("se3", "s5", "s6", "managed SFTP", { style: { ...blue } }),

      // Sources -> Bronze
      mkEdge("sb1", "s2", "b1", "land raw", { style: { ...orange } }),
      mkEdge("sb2", "s4", "b1", "land raw", { style: { ...orange } }),
      mkEdge("sb3", "s6", "b1", "land raw", { style: { ...orange } }),

      // Bronze internal
      mkEdge("bb1", "b1", "b2", "catalog", { style: { ...orange } }),
      mkEdge("bb2", "b2", "b3", "contracts", { style: { ...orange } }),
      mkEdge("bb3", "b1", "b4", "trigger", { style: { ...orange, strokeDasharray: "10 8" } }),

      // Bronze -> Silver
      mkEdge("bv1", "b4", "v1", "run stage", { style: { ...green } }),
      mkEdge("bv2", "v1", "v2", "validate", { style: { ...green } }),
      mkEdge("bv3", "v2", "v3", "reject rows", { style: { ...green, strokeDasharray: "10 8" } }),
      mkEdge("bv4", "v2", "v5", "mask PII", { style: { ...green } }),
      mkEdge("bv5", "v5", "v4", "write clean", { style: { ...green } }),

      // Silver -> Gold
      mkEdge("vg1", "v4", "g1", "model", { style: { ...purple } }),
      mkEdge("vg2", "g1", "g2", "write facts/dims", { style: { ...purple } }),
      mkEdge("vg3", "g2", "g3", "query", { style: { ...purple } }),
      mkEdge("vg4", "g3", "g4", "surface", { style: { ...purple } }),

      // Observability
      mkEdge("ob1", "b4", "g5", "logs + metrics", { style: { ...green, strokeDasharray: "10 8" } }),
      mkEdge("ob2", "g5", "g6", "cost model", { style: { ...green } }),
    ];
  }, []);

  return (
    <div style={{ width: "100%", height: "700px", background: "#ffffff" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.08 }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        minZoom={0.35}
        maxZoom={2}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        preventScrolling={true}
      >
        <Background gap={18} size={1} color="#e5e7eb" />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </div>
  );
}
